import { useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Cpu, HardDrive, MemoryStick, Server } from "lucide-react";
import { useSystemHealthMachine } from "../../lib/api";
import { Chip, FailedToLoad, Heading, Loading, MigrationPending, Notice, ScrollHint, Segmented, endpoint, panel, td, th } from "./healthUi";
import { formatAgo, formatSize, formatUptime, formatWhen } from "./healthFormat";

// Machine tab: the server everything runs on, and our four programs on it.
//   three dials (now) -> who is using it (dev, UAT, the rest) -> each one's programs -> the line over time

const MIGRATION = "20261009110000_add_machine_health.sql";

const COLOURS = { ok: "#16A34A", bad: "#DC2626", unknown: "#9CA3AF" };

// A half-circle dial: how full, with the warning line marked on it.
function Dial({ icon: Icon, label, gauge, warn, children }) {
  const pct = gauge.pct;
  const known = pct != null;
  const radius = 54;
  const half = Math.PI * radius;
  const mark = Math.PI * (1 - warn / 100);
  const colour = COLOURS[gauge.state] || COLOURS.unknown;
  return (
    <li className={`${panel} flex flex-col items-center p-5 text-center`}>
      <span className="flex items-center gap-1.5 self-start text-[13px] font-semibold text-[#111827]">
        <Icon size={16} aria-hidden="true" className="text-[#6B7280]" /> {label}
      </span>
      <svg viewBox="0 0 140 84" className="mt-2 w-full max-w-[190px]" role="img" aria-label={known ? `${label} ${Math.round(pct)} percent used` : `${label} not measured`}>
        <path d="M 16 74 A 54 54 0 0 1 124 74" fill="none" stroke="#F3F4F6" strokeWidth="12" strokeLinecap="round" />
        {known && (
          <path
            d="M 16 74 A 54 54 0 0 1 124 74"
            fill="none"
            stroke={colour}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={`${(half * Math.min(100, Math.max(0, pct))) / 100} ${half}`}
          />
        )}
        {/* where it starts to need attention */}
        <line
          x1={70 + (radius - 9) * Math.cos(mark)} y1={74 - (radius - 9) * Math.sin(mark)}
          x2={70 + (radius + 9) * Math.cos(mark)} y2={74 - (radius + 9) * Math.sin(mark)}
          stroke="#111827" strokeWidth="1.5"
        />
        <text x="70" y="70" textAnchor="middle" fontSize="24" fontWeight="600" fill="#111827">{known ? `${Math.round(pct)}%` : "–"}</text>
      </svg>
      <span className="mt-1 text-[13px] font-medium" style={{ color: gauge.state === "bad" ? "#991B1B" : "#374151" }}>
        {gauge.state === "bad" ? "Needs attention" : gauge.state === "ok" ? "Fine" : "Not measured"}
      </span>
      <span className="mt-1 text-[12px] leading-5 text-[#6B7280]">{children}</span>
    </li>
  );
}

function Specs({ specs }) {
  const cells = [
    ["Server", specs.host || "–"],
    ["CPU cores", specs.cores ?? "–"],
    ["Memory", formatSize(specs.memory_mb)],
    ["Disk", specs.disk_gb != null ? `${Math.round(specs.disk_gb)} GB` : "–"],
    ["Running for", formatUptime(specs.up_seconds)],
  ];
  return (
    <dl className="rounded-2xl bg-[#0F172A] px-5 py-4 text-white sm:px-6">
      <div className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.08em] text-[#94A3B8]">
        <Server size={14} aria-hidden="true" /> Server
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-5">
        {cells.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-[12px] text-[#94A3B8]">{label}</dt>
            <dd className="mt-0.5 break-words text-[15px] font-semibold">{value}</dd>
          </div>
        ))}
      </div>
    </dl>
  );
}

// One colour per user of the server, in the order they are listed.
const SHARE_COLOURS = ["#2667B5", "#7C3AED", "#0D9488", "#D97706"];
const REST_COLOUR = "#94A3B8";

const shareColour = (env, index) => (env.key ? SHARE_COLOURS[index % SHARE_COLOURS.length] : REST_COLOUR);

// One bar split between the environments, everything else, and what is free.
function ShareBar({ label, parts, free }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-semibold text-[#111827]">{label}</span>
        <span className="text-[12px] text-[#6B7280]">{free}</span>
      </div>
      <div
        role="img"
        aria-label={`${label}: ${parts.map((part) => `${part.name} ${part.pct}%`).join(", ")}`}
        className="mt-1.5 flex h-3 w-full overflow-hidden rounded-full bg-[#F3F4F6]"
      >
        {parts.map((part) => part.pct > 0 && <span key={part.name} style={{ width: `${Math.min(100, part.pct)}%`, background: part.colour }} />)}
      </div>
    </div>
  );
}

// Dev and UAT share this server: who is using how much of it right now.
function Usage({ environments }) {
  if (environments.length === 0) return null;
  const memory = environments.map((env, i) => ({ name: env.label, pct: env.memory_pct ?? 0, colour: shareColour(env, i) }));
  const cpu = environments.map((env, i) => ({ name: env.label, pct: env.cpu_pct ?? 0, colour: shareColour(env, i) }));
  const left = (parts) => `${Math.max(0, Math.round(100 - parts.reduce((sum, part) => sum + part.pct, 0)))}% free`;
  return (
    <section className={`${panel} p-5 sm:p-6`}>
      <h2 className="text-[17px] font-semibold text-[#111827]">Server usage</h2>
      <div className="mt-4 flex flex-col gap-4">
        <ShareBar label="Memory" parts={memory} free={left(memory)} />
        <ShareBar label="CPU" parts={cpu} free={left(cpu)} />
      </div>
      <ul className="mt-4 grid gap-3 sm:grid-cols-3">
        {environments.map((env, i) => (
          <li key={env.key || "rest"} className="rounded-xl bg-[#F9FAFB] px-4 py-3">
            <span className="flex items-center gap-2 text-[13px] font-semibold text-[#111827]">
              <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: shareColour(env, i) }} />
              {env.label}
              {env.ours && <span className="rounded bg-[#E0E7FF] px-1.5 py-0.5 text-[11px] font-medium text-[#3730A3]">this one</span>}
            </span>
            <span className="mt-1.5 block text-[13px] tabular-nums text-[#374151]">
              Memory {formatSize(env.memory_mb)} ({env.memory_pct ?? 0}%)
            </span>
            <span className="block text-[13px] tabular-nums text-[#374151]">CPU {env.cpu_pct}%</span>
            {env.processes != null && <span className="block text-[12px] text-[#6B7280]">{env.processes} processes</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

// The four programs of one environment. With more than one on the server, a switch picks which.
function Programs({ environments, fallback }) {
  const real = environments.filter((env) => env.key);
  const [picked, setPicked] = useState("");
  const shown = real.find((env) => env.key === picked) || real.find((env) => env.ours) || real[0];
  const programs = shown ? shown.programs : fallback;
  return (
    <section className={`${panel} p-5 sm:p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="min-w-0 text-[17px] font-semibold text-[#111827]">{shown && real.length > 1 ? `${shown.label} programs` : "Programs"}</h2>
        {real.length > 1 && (
          <Segmented
            label="Environment"
            options={real.map((env) => ({ key: env.key, label: env.label }))}
            isOn={(key) => key === shown.key}
            pick={setPicked}
          />
        )}
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[620px] border-collapse">
          <thead>
            <tr className="border-b border-[#E5E7EB]">
              <th className={th}>Program</th>
              <th className={th}>Status</th>
              <th className={`${th} text-right`}>Running for</th>
              <th className={`${th} text-right`}>Memory</th>
              <th className={`${th} text-right`}>CPU</th>
              <th className={`${th} text-right`}>Processes</th>
            </tr>
          </thead>
          <tbody>
            {programs.map((p) => (
              <tr key={p.key} className="border-b border-[#F3F4F6] last:border-0">
                <td className={`${td} font-semibold text-[#111827]`}>{p.label}</td>
                <td className={td}>
                  {!p.running ? (
                    <Chip tone="bad">Not running</Chip>
                  ) : p.restarted_lately ? (
                    <Chip tone="warn">Started again lately</Chip>
                  ) : (
                    <Chip tone="good">Running</Chip>
                  )}
                </td>
                <td className={`${td} text-right tabular-nums`}>{p.running ? formatUptime(p.up_seconds) : "–"}</td>
                <td className={`${td} text-right tabular-nums`}>{p.running ? formatSize(p.rss_mb) : "–"}</td>
                <td className={`${td} text-right tabular-nums`}>{p.running && p.cpu_pct != null ? `${p.cpu_pct}%` : "–"}</td>
                <td className={`${td} text-right tabular-nums`}>{p.running ? p.processes : "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ScrollHint />
    </section>
  );
}

function OverTime({ series, warn }) {
  if (series.length < 2) return null;
  const data = series.map((p) => ({ at: formatWhen(p.at), CPU: p.cpu_pct, Memory: p.memory_pct, Disk: p.disk_pct }));
  return (
    <section className={`${panel} p-5 sm:p-6`}>
      <h2 className="text-[17px] font-semibold text-[#111827]">Usage over time</h2>
      <div className="mt-4 h-[260px]" role="img" aria-label="CPU, memory and disk in use over time">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            <XAxis dataKey="at" tick={{ fontSize: 11, fill: "#6B7280" }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} minTickGap={48} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#6B7280" }} tickLine={false} axisLine={false} unit="%" />
            <ReferenceLine y={warn} stroke="#DC2626" strokeDasharray="4 4" />
            <Tooltip formatter={(value) => `${value}%`} contentStyle={{ borderRadius: 12, borderColor: "#E5E7EB", fontSize: 13 }} />
            <Line type="monotone" dataKey="CPU" stroke="#2667B5" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="Memory" stroke="#7C3AED" strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="Disk" stroke="#0D9488" strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-[#4B5563]">
        {[["CPU", "#2667B5"], ["Memory", "#7C3AED"], ["Disk", "#0D9488"]].map(([name, colour]) => (
          <span key={name} className="flex items-center gap-1.5"><span className="h-0.5 w-4 rounded" style={{ background: colour }} /> {name}</span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-4 border-t border-dashed border-[#DC2626]" /> {warn}% limit</span>
      </p>
    </section>
  );
}

export default function HealthMachine({ range }) {
  const { data, isLoading, error, refetch } = useSystemHealthMachine({ from: range.from() });
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  if (data?.migration_pending) return <MigrationPending file={MIGRATION} />;
  if (!data.measured) {
    return (
      <Notice>
        {data.sampler_on
          ? "The server has not been measured in this period yet. The first measurement arrives about a minute after the API starts."
          : "Measuring the server is switched off, or its library is not installed on this server yet."}
      </Notice>
    );
  }

  const { gauges, specs, latest, programs, problems } = data;
  const wrong = problems.filter((p) => p.urgent || !/started again/.test(p.what));
  const headline = latest.stale
    ? "The server is not being measured"
    : wrong.length
      ? `${wrong.length} ${wrong.length === 1 ? "thing needs" : "things need"} attention on the server`
      : "The server is healthy";
  return (
    <div title={endpoint("machine")} className="mt-6 flex flex-col gap-5">
      <header role="status" className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: latest.stale || wrong.length ? COLOURS.bad : COLOURS.ok }} />
          <Heading className="text-[15px] font-semibold text-[#111827]">{headline}</Heading>
        </div>
        <span className={`text-[13px] ${latest.stale ? "font-medium text-[#991B1B]" : "text-[#6B7280]"}`}>Measured {formatAgo(latest.seconds_ago)}</span>
      </header>

      {problems.length > 0 && (
        <ul aria-label="What needs attention" className="flex flex-col gap-2">
          {problems.map((p) => {
            // a restart is only worth a mention; the rest need someone
            const mention = !wrong.includes(p);
            return (
              <li
                key={p.what}
                className="rounded-xl border px-4 py-3"
                style={mention ? { borderColor: "#FDE68A", background: "#FFFBEB" } : { borderColor: "#FECACA", background: "#FEF2F2" }}
              >
                <span className="block text-[14px] font-semibold" style={{ color: mention ? "#92400E" : "#991B1B" }}>{p.what}</span>
                <span className="block text-[13px] text-[#374151]">{p.meaning}.</span>
              </li>
            );
          })}
        </ul>
      )}

      <Specs specs={specs} />

      <ul aria-label="In use now" className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Dial icon={Cpu} label="CPU" gauge={gauges.cpu} warn={data.warn_pct}>
          {gauges.cpu.load1 != null ? `Load ${gauges.cpu.load1} across ${gauges.cpu.cores} cores` : `${gauges.cpu.cores ?? "–"} cores`}
        </Dial>
        <Dial icon={MemoryStick} label="Memory" gauge={gauges.memory} warn={data.warn_pct}>
          {formatSize(gauges.memory.used_mb)} of {formatSize(gauges.memory.total_mb)} used
          {gauges.memory.swap_used_mb > 0 && <span className="block font-medium text-[#92400E]">{formatSize(gauges.memory.swap_used_mb)} of swap in use</span>}
        </Dial>
        <Dial icon={HardDrive} label="Disk" gauge={gauges.disk} warn={data.warn_pct}>
          {Math.round(gauges.disk.used_gb)} GB of {Math.round(gauges.disk.total_gb)} GB used
          {gauges.disk.logs_mb != null && <span className="block">Logs take {formatSize(gauges.disk.logs_mb)}</span>}
        </Dial>
      </ul>

      <Usage environments={data.environments || []} />
      <Programs environments={data.environments || []} fallback={programs} />
      <OverTime series={data.series} warn={data.warn_pct} />
    </div>
  );
}
